import React, { createContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import NotificationAlert from "react-notification-alert";

export const ErrorContext = createContext();

export const ErrorProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const notificationAlertRef = useRef(null);

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const notifyError = (message) => {
    if (notificationAlertRef.current) {
      notificationAlertRef.current.notificationAlert({
        place: "tc",
        message: <div>{message}</div>,
        type: "danger",
        icon: "nc-icon nc-bell-55",
        autoDismiss: 5,
      });
    }
  };

  useEffect(() => {
    let isRedirecting = false;

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const isAuthRoute = pathnameRef.current.startsWith("/auth");

        if (error.response) {
          if (error.response.status === 401 && !isAuthRoute && !isRedirecting) {
            isRedirecting = true;

            // Clear auth state — PrivateRoute will catch the missing
            // token on the next render and redirect to login.
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            sessionStorage.removeItem("authUser");

            // Use React Router navigate (not window.location.href)
            // to avoid hard-reload loops from concurrent 401 responses.
            navigateRef.current("/auth/login", { replace: true });

            // Unlock the redirect flag after the transition completes
            // so future 401s on other pages can be caught again.
            setTimeout(() => { isRedirecting = false; }, 1000);
          } else if (error.response.status >= 500) {
            notifyError("Server encountered an error. Please try again later.");
          }
        } else if (error.request) {
          notifyError("Network error. Please check your connection.");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <ErrorContext.Provider value={{ notifyError }}>
      <NotificationAlert ref={notificationAlertRef} />
      {children}
    </ErrorContext.Provider>
  );
};
